// Phase 9.7.10 — Stock Matrix v2 (vendor-grouped, review-gated, in-app + Excel).
//
// Replaces the Phase 9.1 flat heatmap. New workflow:
//
//   Step 1 — Review
//     Load /api/fnb/admin/stock-matrix/preview live from the DB.
//     Operator picks: outlets (checkbox grid), vendor blocks + items
//     inside each block, optional Unconfigured section.
//
//   Step 2 — Matrix
//     POST /api/fnb/admin/stock-matrix/generate with the selections.
//     Renders a vendor-grouped table with sticky left cols + red fill
//     on cells where `current_qty < reorder_level_qty` (strict <).
//     "Download Excel" streams from /api/fnb/admin/stock-matrix/excel/<token>.
//
// Nothing about the old flat heatmap survives — this component IS the
// Stock Matrix page now.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, API, detailFromError } from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";

const RED_CELL_CLASS = "bg-rose-100 text-rose-900 font-medium";
const HEADER_CELL_CLASS = "bg-muted/40 text-[11px] font-semibold uppercase tracking-wide";

function fmtQty(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// CHANGE 5 — hide the "· <sub_categories>" suffix on a vendor header
// when the rendered tag string, post-trim + case-insensitive, exactly
// equals the vendor name. Compound labels (e.g. "Coca Cola, Cold
// Beverages") never collapse to the plain vendor name, so they still
// render.
function shouldShowSubCategoryTag(vendorLabel, subCategories) {
  if (!subCategories || subCategories.length === 0) return false;
  const rendered =
    subCategories.slice(0, 3).join(", ")
    + (subCategories.length > 3 ? ` +${subCategories.length - 3}` : "");
  const renderedNorm = rendered.trim().toLowerCase();
  const nameNorm = (vendorLabel || "").trim().toLowerCase();
  return renderedNorm !== nameNorm;
}

function fmtGeneratedAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// -----------------------------------------------------------------------------
// Selection helpers — Set-in-state mutation done immutably.
// -----------------------------------------------------------------------------

function setWith(prev, key, add) {
  const next = new Set(prev);
  if (add) next.add(key); else next.delete(key);
  return next;
}

// -----------------------------------------------------------------------------

export default function FnbPoStockMatrix() {
  const [step, setStep] = useState("review");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [selectedOutlets, setSelectedOutlets] = useState(new Set());
  const [selectedByVendor, setSelectedByVendor] = useState({});
  const [selectedBySubCategory, setSelectedBySubCategory] = useState({});
  const [expanded, setExpanded] = useState(new Set());
  const [expandedSubCat, setExpandedSubCat] = useState(new Set());
  const [includeUnconfigured, setIncludeUnconfigured] = useState(false);
  const [showUnconfigured, setShowUnconfigured] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [matrix, setMatrix] = useState(null);
  const [matrixToken, setMatrixToken] = useState(null);

  // -------------- data load ----------------------------------------------

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/fnb/admin/stock-matrix/preview");
      setPreview(res.data);
      // On first load, prefill: select every outlet by default (operator
      // can uncheck) and expand nothing. Items start unchecked so the
      // operator has to review actively.
      setSelectedOutlets(new Set((res.data.outlets || []).map((o) => o.location_id)));
      setSelectedByVendor({});
      setSelectedBySubCategory({});
      setExpanded(new Set());
      setExpandedSubCat(new Set());
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  // -------------- derived counts ----------------------------------------

  const totals = useMemo(() => {
    // Section 1 = configured vendors PLUS linked-but-unconfigured items
    // (has_real_link=true). Selection state is flat on `selectedByVendor`
    // keyed by vendor_label. Section 2 = unlinked items grouped by
    // sub_category, keyed on `selectedBySubCategory`.
    const section1Vendors = new Set(
      (preview?.vendors || []).map((v) => v.vendor_label)
    );
    for (const it of (preview?.unconfigured_items || [])) {
      if (it.has_real_link && it.vendor_link_raw) {
        section1Vendors.add(it.vendor_link_raw);
      }
    }
    let section1Selected = 0;
    let vendorsWithSelection = 0;
    for (const label of section1Vendors) {
      const chosen = selectedByVendor[label];
      if (chosen && chosen.size > 0) {
        section1Selected += chosen.size;
        vendorsWithSelection += 1;
      }
    }
    let section2Selected = 0;
    for (const chosen of Object.values(selectedBySubCategory)) {
      if (chosen && chosen.size > 0) section2Selected += chosen.size;
    }
    return {
      outlets: selectedOutlets.size,
      totalOutlets: (preview?.outlets || []).length,
      section1Selected,
      section2Selected,
      totalSelected: section1Selected + section2Selected,
      vendorsWithSelection,
      totalVendors: section1Vendors.size,
      unconfigured: (preview?.unconfigured_items || []).length,
    };
  }, [preview, selectedByVendor, selectedBySubCategory, selectedOutlets]);

  const canGenerate =
    totals.outlets > 0 &&
    (totals.section1Selected > 0 || (includeUnconfigured && totals.unconfigured > 0));

  // -------------- selection actions -------------------------------------

  const toggleOutlet = (lid, add) => setSelectedOutlets((prev) => setWith(prev, lid, add));
  const selectAllOutlets = () =>
    setSelectedOutlets(new Set((preview?.outlets || []).map((o) => o.location_id)));
  const clearAllOutlets = () => setSelectedOutlets(new Set());

  const toggleItem = (vendorLabel, sku, add) => {
    setSelectedByVendor((prev) => {
      const cur = new Set(prev[vendorLabel] || []);
      if (add) cur.add(sku); else cur.delete(sku);
      const next = { ...prev };
      if (cur.size === 0) delete next[vendorLabel];
      else next[vendorLabel] = cur;
      return next;
    });
  };

  const setVendorAll = (vendor, add) => {
    setSelectedByVendor((prev) => {
      const next = { ...prev };
      if (add) next[vendor.vendor_label] = new Set(vendor.items.map((i) => i.item_sku));
      else delete next[vendor.vendor_label];
      return next;
    });
  };

  const selectAllVendors = ({ filteredSection1Vendors, filteredSection2SubCategories, searchActive }) => {
    // CHANGE D — bulk select every currently-visible item across both
    // Section 1 (vendor blocks) and Section 2 (sub_category groups).
    // Search-aware: union with existing selection when a query is
    // active (hidden items untouched); with no query, replace.
    setSelectedByVendor((prev) => {
      if (!searchActive) {
        const next = {};
        for (const v of filteredSection1Vendors) {
          next[v.vendor_label] = new Set(v.items.map((i) => i.item_sku));
        }
        return next;
      }
      const next = { ...prev };
      for (const v of filteredSection1Vendors) {
        const cur = new Set(next[v.vendor_label] || []);
        for (const it of v.items) cur.add(it.item_sku);
        next[v.vendor_label] = cur;
      }
      return next;
    });
    setSelectedBySubCategory((prev) => {
      if (!searchActive) {
        const next = {};
        for (const g of filteredSection2SubCategories) {
          next[g.label] = new Set(g.items.map((i) => i.item_sku));
        }
        return next;
      }
      const next = { ...prev };
      for (const g of filteredSection2SubCategories) {
        const cur = new Set(next[g.label] || []);
        for (const it of g.items) cur.add(it.item_sku);
        next[g.label] = cur;
      }
      return next;
    });
  };

  const clearAllVendors = ({ filteredSection1Vendors, filteredSection2SubCategories, searchActive } = {}) => {
    // When a search is active, clear only the currently-visible items;
    // hidden selections survive. With no search, wipe everything.
    setSelectedByVendor((prev) => {
      if (!searchActive) return {};
      const next = { ...prev };
      for (const v of (filteredSection1Vendors || [])) {
        const cur = new Set(next[v.vendor_label] || []);
        for (const it of v.items) cur.delete(it.item_sku);
        if (cur.size === 0) delete next[v.vendor_label];
        else next[v.vendor_label] = cur;
      }
      return next;
    });
    setSelectedBySubCategory((prev) => {
      if (!searchActive) return {};
      const next = { ...prev };
      for (const g of (filteredSection2SubCategories || [])) {
        const cur = new Set(next[g.label] || []);
        for (const it of g.items) cur.delete(it.item_sku);
        if (cur.size === 0) delete next[g.label];
        else next[g.label] = cur;
      }
      return next;
    });
  };

  // Section 2 selection handlers — mirror the Section 1 ones but
  // write into `selectedBySubCategory` (keys = sub_category labels).
  const subcatToggleItem = (subCategoryLabel, sku, add) => {
    setSelectedBySubCategory((prev) => {
      const cur = new Set(prev[subCategoryLabel] || []);
      if (add) cur.add(sku); else cur.delete(sku);
      const next = { ...prev };
      if (cur.size === 0) delete next[subCategoryLabel];
      else next[subCategoryLabel] = cur;
      return next;
    });
  };

  const subcatSetGroupAll = (group, add) => {
    setSelectedBySubCategory((prev) => {
      const next = { ...prev };
      if (add) next[group.label] = new Set(group.items.map((i) => i.item_sku));
      else delete next[group.label];
      return next;
    });
  };

  const toggleExpandSubCat = (label) => setExpandedSubCat((prev) => {
    const next = new Set(prev);
    if (next.has(label)) next.delete(label); else next.add(label);
    return next;
  });

  const toggleExpand = (label) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(label)) next.delete(label); else next.add(label);
    return next;
  });

  const expandAll = () => setExpanded(new Set((preview?.vendors || []).map((v) => v.vendor_label)));
  const collapseAll = () => setExpanded(new Set());

  // -------------- generate ----------------------------------------------

  const generate = async () => {
    if (!canGenerate) return;
    setGenerating(true);
    try {
      const body = {
        outlet_ids: [...selectedOutlets],
        vendor_selections: Object.entries(selectedByVendor).map(
          ([vendor_label, skus]) => ({ vendor_label, item_skus: [...skus] }),
        ),
        include_unconfigured: includeUnconfigured,
      };
      const res = await api.post("/fnb/admin/stock-matrix/generate", body);
      setMatrix(res.data.matrix);
      setMatrixToken(res.data.matrix_token);
      setStep("matrix");
      toast.success("Stock matrix generated");
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setGenerating(false);
    }
  };

  const backToReview = () => {
    setStep("review");
    setMatrix(null);
    setMatrixToken(null);
  };

  // -------------- render -------------------------------------------------

  if (loading) {
    return (
      <div className="space-y-6" data-testid="stock-matrix-loading">
        <PageHeader />
        <Card><CardContent className="py-16 text-center text-sm text-muted-foreground">
          Loading preview…
        </CardContent></Card>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <Alert variant="destructive"><AlertTitle>Preview unavailable</AlertTitle>
          <AlertDescription>Please refresh and try again.</AlertDescription></Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fnb-po-stock-matrix">
      <PageHeader step={step} onReload={loadPreview} onBack={backToReview} />
      {step === "review" ? (
        <ReviewScreen
          preview={preview}
          selectedOutlets={selectedOutlets}
          selectedByVendor={selectedByVendor}
          selectedBySubCategory={selectedBySubCategory}
          expanded={expanded}
          expandedSubCat={expandedSubCat}
          totals={totals}
          canGenerate={canGenerate}
          generating={generating}
          includeUnconfigured={includeUnconfigured}
          showUnconfigured={showUnconfigured}
          onToggleOutlet={toggleOutlet}
          onSelectAllOutlets={selectAllOutlets}
          onClearAllOutlets={clearAllOutlets}
          onToggleItem={toggleItem}
          onSetVendorAll={setVendorAll}
          onSubcatToggleItem={subcatToggleItem}
          onSubcatSetGroupAll={subcatSetGroupAll}
          onSelectAllVendors={selectAllVendors}
          onClearAllVendors={clearAllVendors}
          onToggleExpand={toggleExpand}
          onToggleExpandSubCat={toggleExpandSubCat}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll}
          onSetIncludeUnconfigured={setIncludeUnconfigured}
          onSetShowUnconfigured={setShowUnconfigured}
          onGenerate={generate}
        />
      ) : (
        <MatrixScreen matrix={matrix} token={matrixToken} onBack={backToReview} />
      )}
    </div>
  );
}

// =============================================================================
// PageHeader — brand row above both screens
// =============================================================================

function PageHeader({ step = "review", onReload, onBack }) {
  return (
    <header className="flex items-start justify-between gap-3">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="stock-matrix-heading">
          Stock Matrix
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          {step === "review"
            ? "Step 1 · Review active outlets and items per vendor. Live from the DB — no snapshotting."
            : "Step 2 · Vendor-grouped snapshot. Red cells: current stock is below the reorder threshold."}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {step === "review" ? (
          <Button
            variant="ghost" size="sm" onClick={onReload}
            data-testid="stock-matrix-reload"
          >↻ Reload</Button>
        ) : (
          <Button
            variant="ghost" size="sm" onClick={onBack}
            data-testid="stock-matrix-back-to-review"
          >← Back to review</Button>
        )}
        <Button asChild variant="ghost" size="sm" data-testid="stock-matrix-back-dash">
          <Link to="/tools/fnb-po">Dashboard</Link>
        </Button>
      </div>
    </header>
  );
}

// =============================================================================
// Review screen
// =============================================================================

function ReviewScreen(props) {
  const {
    preview, selectedOutlets, selectedByVendor,
    selectedBySubCategory, expanded, expandedSubCat, totals,
    canGenerate, generating, includeUnconfigured, showUnconfigured,
    onToggleOutlet, onSelectAllOutlets, onClearAllOutlets,
    onToggleItem, onSetVendorAll,
    onSubcatToggleItem, onSubcatSetGroupAll,
    onSelectAllVendors, onClearAllVendors,
    onToggleExpand, onToggleExpandSubCat, onExpandAll, onCollapseAll,
    onSetIncludeUnconfigured, onSetShowUnconfigured,
    onGenerate,
  } = props;

  const outlets = preview.outlets || [];
  const rawVendors = useMemo(() => preview.vendors || [], [preview.vendors]);
  const unconfigured = useMemo(
    () => preview.unconfigured_items || [],
    [preview.unconfigured_items],
  );

  // ---- Section 1 aggregation ------------------------------------------
  // Vendors[] (configured) UNION linked-but-unconfigured items,
  // grouped by vendor_label. Configured items are marked has_config=true;
  // bucket-B fillers are marked has_config=false so the UI can flag
  // them with the "needs config" ⚠️ badge.
  const section1Vendors = useMemo(() => {
    const buckets = new Map();
    for (const v of rawVendors) {
      const items = (v.items || []).map((it) => ({ ...it, has_config: true }));
      buckets.set(v.vendor_label, {
        vendor_label: v.vendor_label,
        is_virtual: v.is_virtual,
        sub_categories: [...(v.sub_categories || [])],
        items,
      });
    }
    for (const it of unconfigured) {
      if (!it.has_real_link) continue;
      const label = it.vendor_link_raw || "";
      if (!label) continue;
      const filler = { ...it, has_config: false };
      const existing = buckets.get(label);
      if (existing) {
        existing.items.push(filler);
      } else {
        buckets.set(label, {
          vendor_label: label,
          is_virtual: false,
          sub_categories: [],
          items: [filler],
        });
      }
    }
    return [...buckets.values()].sort(
      (a, b) => a.vendor_label.toLowerCase().localeCompare(b.vendor_label.toLowerCase()),
    );
  }, [rawVendors, unconfigured]);

  // ---- Section 2 grouping ---------------------------------------------
  // Unlinked items grouped by sub_category. Empty/null/missing →
  // "Uncategorized". Sorted by item count DESC.
  const section2SubCategories = useMemo(() => {
    const buckets = new Map();
    for (const it of unconfigured) {
      if (it.has_real_link) continue;
      const key =
        (it.sub_category && String(it.sub_category).trim()) || "Uncategorized";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(it);
    }
    const arr = [...buckets.entries()].map(([label, items]) => {
      const allExcluded =
        items.length > 0 && items.every((r) => r.item_scope === "excluded");
      const anyExcluded = items.some((r) => r.item_scope === "excluded");
      return {
        label,
        items,
        allExcluded,
        // "mixed" = has at least one excluded AND at least one non-excluded
        isMixed: !allExcluded && anyExcluded,
      };
    });
    arr.sort((a, b) => b.items.length - a.items.length);
    return arr;
  }, [unconfigured]);

  // ---- Global search ---------------------------------------------------
  const [searchTerm, setSearchTerm] = useState("");
  const q = searchTerm.trim().toLowerCase();
  const matchesQuery = useCallback((it) => {
    if (!q) return true;
    const sku = (it.item_sku || "").toLowerCase();
    const nm = (it.item_name || "").toLowerCase();
    return sku.includes(q) || nm.includes(q);
  }, [q]);

  const filteredSection1Vendors = useMemo(() => {
    if (!q) return section1Vendors;
    const out = [];
    for (const v of section1Vendors) {
      const items = (v.items || []).filter(matchesQuery);
      if (items.length > 0) out.push({ ...v, items });
    }
    return out;
  }, [section1Vendors, q, matchesQuery]);

  const filteredSection2SubCategories = useMemo(() => {
    if (!q) return section2SubCategories;
    const out = [];
    for (const g of section2SubCategories) {
      const items = (g.items || []).filter(matchesQuery);
      if (items.length > 0) {
        const allExcluded =
          items.length > 0 && items.every((r) => r.item_scope === "excluded");
        const anyExcluded = items.some((r) => r.item_scope === "excluded");
        out.push({
          label: g.label,
          items,
          allExcluded,
          isMixed: !allExcluded && anyExcluded,
        });
      }
    }
    return out;
  }, [section2SubCategories, q, matchesQuery]);

  // Force-expand matched groups while search is active (both sections).
  const effectiveExpanded = useMemo(() => {
    if (!q) return expanded;
    return new Set(filteredSection1Vendors.map((v) => v.vendor_label));
  }, [q, expanded, filteredSection1Vendors]);
  const effectiveExpandedSubCat = useMemo(() => {
    if (!q) return expandedSubCat;
    return new Set(filteredSection2SubCategories.map((g) => g.label));
  }, [q, expandedSubCat, filteredSection2SubCategories]);

  const section2VisibleCount = filteredSection2SubCategories.reduce(
    (n, g) => n + g.items.length,
    0,
  );
  const section2TotalCount = section2SubCategories.reduce(
    (n, g) => n + g.items.length,
    0,
  );

  const noMatchesAnywhere =
    q.length > 0 && filteredSection1Vendors.length === 0 && section2VisibleCount === 0;

  return (
    <div className="space-y-6 pb-32">
      <section className="space-y-5">
        {/* Outlets */}
        <Card data-testid="outlets-card">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">Outlets on the matrix</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Every outlet with at least one active-here item.
                  HO and fully-inactive outlets are excluded.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={onSelectAllOutlets}
                  data-testid="outlets-select-all">All</Button>
                <Button size="sm" variant="ghost" onClick={onClearAllOutlets}
                  data-testid="outlets-clear-all">None</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
              {outlets.map((o) => {
                const checked = selectedOutlets.has(o.location_id);
                return (
                  <label
                    key={o.location_id}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                    data-testid={`outlet-row-${o.location_id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => onToggleOutlet(o.location_id, !!v)}
                      data-testid={`outlet-checkbox-${o.location_id}`}
                    />
                    <span className="truncate">{o.display_name}</span>
                    {o.store_code && (
                      <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                        {o.store_code}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline"
              onClick={() => onSelectAllVendors({
                filteredSection1Vendors,
                filteredSection2SubCategories,
                searchActive: q.length > 0,
              })}
              data-testid="vendors-select-all">Select all items</Button>
            <Button size="sm" variant="outline"
              onClick={() => onClearAllVendors({
                filteredSection1Vendors,
                filteredSection2SubCategories,
                searchActive: q.length > 0,
              })}
              data-testid="vendors-clear-all">Clear all</Button>
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search SKU or item name…"
              className="h-8 text-xs w-56"
              data-testid="stock-matrix-search-input"
            />
            <Button size="sm" variant="ghost" onClick={onExpandAll}
              data-testid="vendors-expand-all">Expand all</Button>
            <Button size="sm" variant="ghost" onClick={onCollapseAll}
              data-testid="vendors-collapse-all">Collapse all</Button>
          </div>
        </div>

        {noMatchesAnywhere && (
          <div
            className="text-sm text-muted-foreground italic px-3 py-6 border border-dashed border-border/60 rounded-lg text-center"
            data-testid="stock-matrix-search-empty"
          >
            No items match your search.
          </div>
        )}

        {/* ============ Section 1 — Vendors ============ */}
        {filteredSection1Vendors.length > 0 && (
          <div className="space-y-3" data-testid="section-1-vendors">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold px-1">
              Vendors ({filteredSection1Vendors.length})
            </div>
            {filteredSection1Vendors.map((v) => (
              <VendorBlock
                key={v.vendor_label}
                vendor={v}
                expanded={effectiveExpanded.has(v.vendor_label)}
                selected={selectedByVendor[v.vendor_label] || new Set()}
                onToggleExpand={() => onToggleExpand(v.vendor_label)}
                onToggleItem={onToggleItem}
                onSetVendorAll={onSetVendorAll}
              />
            ))}
          </div>
        )}

        {/* ============ Section 2 — Items without a vendor ============ */}
        {section2TotalCount > 0 && (
          <div className="space-y-3" data-testid="section-2-unlinked">
            <div className="flex items-center gap-2 px-1">
              <Checkbox
                checked={includeUnconfigured}
                onCheckedChange={(v) => onSetIncludeUnconfigured(!!v)}
                data-testid="section-2-include-checkbox"
              />
              <button
                onClick={() => onSetShowUnconfigured(!showUnconfigured)}
                className="flex items-center gap-2 flex-1 min-w-0 text-left"
                data-testid="section-2-toggle"
              >
                <span className={`inline-block transition-transform text-muted-foreground text-xs ${showUnconfigured ? "rotate-90" : ""}`}>
                  ▶
                </span>
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Items without a vendor ({section2TotalCount} items)
                </span>
              </button>
            </div>
            {showUnconfigured && (
              <div className="space-y-2 pl-2">
                {filteredSection2SubCategories.map((g) => (
                  <Section2Group
                    key={g.label}
                    group={g}
                    expanded={effectiveExpandedSubCat.has(g.label)}
                    selected={selectedBySubCategory[g.label] || new Set()}
                    onToggleExpand={() => onToggleExpandSubCat(g.label)}
                    onToggleItem={onSubcatToggleItem}
                    onSetGroupAll={onSubcatSetGroupAll}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Fixed action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-background border-t border-border/60 shadow-lg">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div className="flex items-center gap-4 text-sm flex-wrap min-w-0">
            <span data-testid="action-bar-outlets">
              <b>{totals.outlets}</b>/{totals.totalOutlets} outlets
            </span>
            <Separator orientation="vertical" className="h-4" />
            <span data-testid="action-bar-items">
              <b>{totals.section1Selected}</b> vendor items + <b>{totals.section2Selected}</b> unlinked items
              {" · "}
              <b>{totals.totalSelected}</b> total selected
            </span>
            {includeUnconfigured && totals.unconfigured > 0 && (
              <>
                <Separator orientation="vertical" className="h-4" />
                <span data-testid="action-bar-unconfigured">
                  + <b>{totals.unconfigured}</b> unconfigured (auto)
                </span>
              </>
            )}
          </div>
          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={onGenerate}
              disabled={!canGenerate || generating}
              data-testid="generate-matrix-btn"
            >
              {generating ? "Generating…" : "Generate matrix →"}
            </Button>
          </div>
          <div className="min-w-[240px]" aria-hidden="true" />
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground px-1" data-testid="preview-snapshot-line">
        Snapshot: <span className="font-mono">{preview.snapshot_id?.slice(0, 8) || "—"}</span>
        {" · "}Live @ {fmtGeneratedAt(preview.generated_at)}
      </div>
    </div>
  );
}

// =============================================================================
// Vendor block accordion
// =============================================================================

function VendorBlock({ vendor, expanded, selected, onToggleExpand, onToggleItem, onSetVendorAll }) {
  const allSelected = selected.size === vendor.items.length && vendor.items.length > 0;
  const someSelected = selected.size > 0 && !allSelected;
  const needsConfigCount = vendor.items.filter((it) => it.has_config === false).length;

  return (
    <Card
      id={`vendor-${slug(vendor.vendor_label)}`}
      className="scroll-mt-4"
      data-testid={`vendor-block-${slug(vendor.vendor_label)}`}
    >
      <CardHeader className="py-2.5 px-4 flex-row items-center gap-2 space-y-0">
        <Checkbox
          checked={allSelected ? true : someSelected ? "indeterminate" : false}
          onCheckedChange={(v) => onSetVendorAll(vendor, !!v)}
          data-testid={`vendor-block-select-all-${slug(vendor.vendor_label)}`}
        />
        <button
          onClick={onToggleExpand}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
          data-testid={`vendor-block-toggle-${slug(vendor.vendor_label)}`}
        >
          <span className={`inline-block transition-transform text-muted-foreground text-xs ${expanded ? "rotate-90" : ""}`}>
            ▶
          </span>
          <CardTitle className="text-sm font-semibold truncate">
            {vendor.vendor_label}
          </CardTitle>
          {vendor.is_virtual && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 bg-amber-50 text-amber-800 border-amber-200">
              virtual
            </Badge>
          )}
          {vendor.sub_categories?.length > 0 && shouldShowSubCategoryTag(vendor.vendor_label, vendor.sub_categories) && (
            <span className="text-[11px] text-muted-foreground truncate">
              · {vendor.sub_categories.slice(0, 3).join(", ")}
              {vendor.sub_categories.length > 3 && ` +${vendor.sub_categories.length - 3}`}
            </span>
          )}
        </button>
        <span className="text-[11px] text-muted-foreground shrink-0" data-testid={`vendor-block-count-${slug(vendor.vendor_label)}`}>
          {vendor.items.length} items
          {needsConfigCount > 0 && (
            <span className="text-amber-700"> ({needsConfigCount} need config)</span>
          )}
        </span>
        <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0 shrink-0">
          {selected.size}/{vendor.items.length}
        </Badge>
      </CardHeader>
      {expanded && (
        <CardContent className="p-0 border-t border-border/40">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className={HEADER_CELL_CLASS}>
                <tr>
                  <th className="text-left px-3 py-1.5 w-8"></th>
                  <th className="text-left px-2 py-1.5">Item</th>
                  <th className="text-left px-2 py-1.5">Sub-category</th>
                  <th className="text-right px-2 py-1.5">Reorder</th>
                  <th className="text-left px-2 py-1.5">Unit</th>
                  <th className="text-right px-2 py-1.5">Active outlets</th>
                </tr>
              </thead>
              <tbody>
                {vendor.items.map((item) => {
                  const checked = selected.has(item.item_sku);
                  const activeOutlets = Object.values(item.per_outlet || {})
                    .filter((c) => c.is_active_here).length;
                  const testid = `item-row-${slug(vendor.vendor_label)}-${item.item_sku}`;
                  const needsConfig = item.has_config === false;
                  return (
                    <tr
                      key={item.item_sku}
                      className={`border-t border-border/30 ${checked ? "bg-muted/30" : ""}`}
                      data-testid={testid}
                    >
                      <td className="px-3 py-1.5">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => onToggleItem(vendor.vendor_label, item.item_sku, !!v)}
                          data-testid={`item-checkbox-${item.item_sku}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="font-medium flex items-center gap-1.5">
                          {item.is_active_on_any_outlet === false && (
                            <span
                              className="text-amber-700 text-[10px] bg-amber-50 px-1.5 py-0.5 rounded shrink-0"
                              title="Not active on any outlet — reorder rule exists but no recent stock activity"
                              data-testid="stock-matrix-inactive-chip"
                            >
                              inactive
                            </span>
                          )}
                          {item.link_source === "rates_only" && (
                            <span
                              className="text-slate-500 text-[10px] bg-slate-100 px-1.5 py-0.5 rounded shrink-0"
                              title="Vendor known via rate card, no explicit vendor link"
                              data-testid="stock-matrix-rates-only-chip"
                            >
                              rates-only
                            </span>
                          )}
                          {needsConfig && (
                            <AlertTriangle
                              className="h-3.5 w-3.5 text-amber-500 shrink-0"
                              aria-label="No reorder config"
                              data-testid={`item-needs-config-${item.item_sku}`}
                            >
                              <title>No reorder config</title>
                            </AlertTriangle>
                          )}
                          <span>{item.item_name}</span>
                        </div>
                        <div className="font-mono text-[10px] text-muted-foreground">
                          {item.item_sku}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[220px]">
                        {item.sub_category || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono">
                        {needsConfig
                          ? <span className="text-muted-foreground/50">—</span>
                          : fmtQty(item.reorder_level_qty)}
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground">
                        {item.measuring_unit || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right text-muted-foreground">
                        {activeOutlets}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
}


// =============================================================================
// Section 2 group — sub_category-grouped bucket of items with no vendor
// link. One level of accordion; no nested vendor grouping. Selection
// state is flat on `selectedBySubCategory[label]`.
// =============================================================================

function Section2Group({
  group, expanded, selected, onToggleExpand, onToggleItem, onSetGroupAll,
}) {
  const items = group.items || [];
  const allSelected = selected.size === items.length && items.length > 0;
  const someSelected = selected.size > 0 && !allSelected;
  const slugLabel = slug(group.label);

  return (
    <Card
      className="border-border/50 shadow-none"
      data-testid={`section-2-group-${slugLabel}`}
    >
      <CardHeader className="py-2 px-3 flex-row items-center gap-2 space-y-0 bg-background">
        <Checkbox
          checked={allSelected ? true : someSelected ? "indeterminate" : false}
          onCheckedChange={(v) => onSetGroupAll(group, !!v)}
          data-testid={`section-2-group-select-all-${slugLabel}`}
        />
        <button
          onClick={onToggleExpand}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
          data-testid={`section-2-group-toggle-${slugLabel}`}
        >
          <span className={`inline-block transition-transform text-muted-foreground text-xs ${expanded ? "rotate-90" : ""}`}>
            ▶
          </span>
          <CardTitle className="text-sm font-semibold truncate">
            {group.label}
          </CardTitle>
          <span className="text-[11px] text-muted-foreground shrink-0">
            {items.length} items
          </span>
          {group.allExcluded && (
            <span
              className="text-slate-500 text-[10px] bg-slate-100 px-1.5 py-0.5 rounded shrink-0"
              data-testid={`section-2-group-all-excluded-${slugLabel}`}
            >
              excluded scope
            </span>
          )}
        </button>
        <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0 shrink-0">
          {selected.size}/{items.length}
        </Badge>
      </CardHeader>
      {expanded && (
        <CardContent className="p-0 border-t border-border/40">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className={HEADER_CELL_CLASS}>
                <tr>
                  <th className="text-left px-3 py-1.5 w-8"></th>
                  <th className="text-left px-2 py-1.5">Item</th>
                  <th className="text-left px-2 py-1.5">Unit</th>
                  <th className="text-right px-2 py-1.5">Active outlets</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const checked = selected.has(item.item_sku);
                  const activeOutlets = Object.values(item.per_outlet || {})
                    .filter((c) => c.is_active_here).length;
                  const isExcludedRow = item.item_scope === "excluded";
                  return (
                    <tr
                      key={item.item_sku}
                      className={`border-t border-border/30 ${checked ? "bg-muted/40" : ""}`}
                      data-testid={`section-2-item-${slugLabel}-${item.item_sku}`}
                    >
                      <td className="px-3 py-1.5">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => onToggleItem(group.label, item.item_sku, !!v)}
                          data-testid={`section-2-item-checkbox-${item.item_sku}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="font-medium flex items-center gap-1.5">
                          <span>{item.item_name}</span>
                          {group.isMixed && isExcludedRow && (
                            <span
                              className="text-slate-500 text-[10px] bg-slate-100 px-1.5 py-0.5 rounded shrink-0"
                              data-testid={`section-2-item-excluded-${item.item_sku}`}
                            >
                              excluded scope
                            </span>
                          )}
                        </div>
                        <div className="font-mono text-[10px] text-muted-foreground">
                          {item.item_sku}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground">
                        {item.measuring_unit || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right text-muted-foreground">
                        {activeOutlets}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
}


// =============================================================================
// Matrix screen — vendor-grouped table with red-fill
// =============================================================================

function MatrixScreen({ matrix, token, onBack }) {
  if (!matrix) return null;
  const outlets = matrix.outlets || [];
  const vendors = matrix.vendors || [];
  const unconfigured = matrix.unconfigured_items || [];
  const includeUnc = matrix.include_unconfigured && unconfigured.length > 0;

  const downloadHref = token ? `${API}/fnb/admin/stock-matrix/excel/${token}` : "#";

  return (
    <div className="space-y-4" data-testid="matrix-screen">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm">
              <span className="font-semibold">{vendors.length} vendor{vendors.length === 1 ? "" : "s"}</span>
              {" · "}
              <span>{vendors.reduce((n, v) => n + v.items.length, 0)} items</span>
              {includeUnc && <span> + {unconfigured.length} unconfigured</span>}
              {" · "}
              <span>{outlets.length} outlets</span>
              {" · "}
              <span className="text-muted-foreground">
                Generated {fmtGeneratedAt(matrix.generated_at)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={downloadHref}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
                data-testid="download-excel-btn"
              >↓ Download Excel</a>
              <Button variant="ghost" size="sm" onClick={onBack}
                data-testid="matrix-back-btn">← Back to review</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 border-t border-border/40">
          {vendors.length === 0 && !includeUnc ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No items selected.
            </div>
          ) : (
            <MatrixTable outlets={outlets} vendors={vendors}
              unconfigured={includeUnc ? unconfigured : []} />
          )}
        </CardContent>
      </Card>
      <MatrixLegend />
    </div>
  );
}

function MatrixLegend() {
  return (
    <div className="flex items-center gap-3 text-[11px] text-muted-foreground px-1">
      <span className="uppercase tracking-wider font-semibold">Legend</span>
      <span className={`px-2 py-0.5 rounded ${RED_CELL_CLASS}`}>current &lt; reorder</span>
      <span>· strict less-than · mirrors reference sheet rule</span>
    </div>
  );
}

function MatrixTable({ outlets, vendors, unconfigured }) {
  return (
    <div className="overflow-auto max-h-[70vh]" data-testid="matrix-table-scroller">
      <table className="border-collapse text-xs" data-testid="matrix-table">
        <thead className="sticky top-0 z-20 bg-background shadow-sm">
          <tr>
            <th className={`sticky left-0 z-30 bg-background text-left px-3 py-2 border-b border-r border-border/60 min-w-[140px] ${HEADER_CELL_CLASS}`}>
              Sub Category
            </th>
            <th className={`sticky left-[140px] z-30 bg-background text-left px-2 py-2 border-b border-r border-border/60 min-w-[260px] ${HEADER_CELL_CLASS}`}>
              NAME
            </th>
            <th className={`text-right px-2 py-2 border-b border-border/60 min-w-[80px] ${HEADER_CELL_CLASS}`}>
              Reorder
            </th>
            <th className={`text-left px-2 py-2 border-b border-border/60 min-w-[70px] ${HEADER_CELL_CLASS}`}>
              Unit
            </th>
            {outlets.map((o) => (
              <th
                key={o.location_id}
                className={`text-right px-2 py-2 border-b border-border/60 whitespace-nowrap min-w-[110px] ${HEADER_CELL_CLASS}`}
                title={o.display_name}
              >
                Store-{o.display_name.length > 16 ? o.display_name.slice(0, 14) + "…" : o.display_name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {vendors.map((v) => (
            <VendorRowGroup key={v.vendor_label} vendor={v} outlets={outlets} />
          ))}
          {unconfigured.length > 0 && (
            <UnconfiguredRowGroup rows={unconfigured} outlets={outlets} />
          )}
        </tbody>
      </table>
    </div>
  );
}

function VendorRowGroup({ vendor, outlets }) {
  return (
    <>
      {vendor.items.map((item, idx) => {
        const ro = item.reorder_level_qty;
        const roNum = typeof ro === "number" ? ro : null;
        return (
          <tr key={item.item_sku}
            className="border-t border-border/30 hover:bg-muted/20"
            data-testid={`matrix-row-${item.item_sku}`}>
            <td className="sticky left-0 z-10 bg-background text-left px-3 py-1.5 border-r border-border/40 font-medium">
              {idx === 0 ? (
                <span className="inline-flex items-center gap-1">
                  {vendor.vendor_label}
                  {vendor.is_virtual && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0 bg-amber-50 text-amber-800 border-amber-200">
                      virtual
                    </Badge>
                  )}
                </span>
              ) : (
                <span className="text-muted-foreground/40">{vendor.vendor_label}</span>
              )}
            </td>
            <td className="sticky left-[140px] z-10 bg-background text-left px-2 py-1.5 border-r border-border/40">
              <div className="font-medium truncate max-w-[260px]" title={item.item_name}>
                {item.item_name}
              </div>
              <div className="font-mono text-[10px] text-muted-foreground">{item.item_sku}</div>
            </td>
            <td className="text-right px-2 py-1.5 font-mono">{fmtQty(ro)}</td>
            <td className="text-left px-2 py-1.5 text-muted-foreground">
              {item.measuring_unit || "—"}
            </td>
            {outlets.map((o) => {
              const cell = (item.per_outlet || {})[o.location_id];
              const qty = cell?.current_qty ?? 0;
              const isRed = roNum !== null && qty < roNum;
              return (
                <td
                  key={o.location_id}
                  className={`text-right px-2 py-1.5 font-mono ${isRed ? RED_CELL_CLASS : ""}`}
                  data-testid={`matrix-cell-${item.item_sku}-${o.location_id}`}
                  title={`${o.display_name}: ${qty}${roNum !== null ? ` (reorder ${roNum})` : ""}`}
                >
                  {qty === 0 && !isRed ? (
                    <span className="text-muted-foreground/50">0</span>
                  ) : fmtQty(qty)}
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
}

function UnconfiguredRowGroup({ rows, outlets }) {
  return (
    <>
      <tr className="border-t-2 border-border/60 bg-muted/50">
        <td colSpan={4 + outlets.length} className="px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
          Unconfigured items ({rows.length}) — no reorder threshold, no red-fill applied
        </td>
      </tr>
      {rows.map((r) => (
        <tr key={r.item_sku} className="border-t border-border/30"
          data-testid={`matrix-unconfigured-row-${r.item_sku}`}>
          <td className="sticky left-0 z-10 bg-background text-left px-3 py-1.5 border-r border-border/40 italic text-muted-foreground">
            {r.vendor_name_raw || "—"}
          </td>
          <td className="sticky left-[140px] z-10 bg-background text-left px-2 py-1.5 border-r border-border/40">
            <div className="font-medium truncate max-w-[260px]">{r.item_name}</div>
            <div className="font-mono text-[10px] text-muted-foreground">{r.item_sku}</div>
          </td>
          <td className="text-right px-2 py-1.5 text-muted-foreground">—</td>
          <td className="text-left px-2 py-1.5 text-muted-foreground">{r.measuring_unit || "—"}</td>
          {outlets.map((o) => {
            const qty = ((r.per_outlet || {})[o.location_id] || {}).current_qty ?? 0;
            return (
              <td key={o.location_id} className="text-right px-2 py-1.5 font-mono text-muted-foreground">
                {qty === 0 ? <span className="text-muted-foreground/50">0</span> : fmtQty(qty)}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

// =============================================================================
// utility
// =============================================================================

function slug(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}
